import {Box3,Matrix4,Object3D} from 'three';
import {normalizeFurnishing,furnishingSize,isWallFurnishing,type Furnishing} from './room-furnishings';
/** Conservative visual access space, not a measured exit route or code certification.
 * Protect every side because ladder/stair entry can move with bunk configuration. */
export const BUNK_ACCESS_BUFFER_INCHES=36;
// Composition limits, not inferred room dimensions or building-code clearances.
export const DECOR_MAX_BUNK_DISTANCE_INCHES={rug:36,dresser:60,beanbag:60,tv:60,painting:60,wallLight:60,mirror:60,ceilingFan:72} as const;
export function footprintDistance(a:Footprint,b:Footprint){return Math.hypot(Math.max(0,a.minX-b.maxX,b.minX-a.maxX),Math.max(0,a.minZ-b.maxZ,b.minZ-a.maxZ));}
export type Footprint={minX:number;maxX:number;minZ:number;maxZ:number};
const overlaps=(a:Footprint,b:Footprint)=>a.minX<=b.maxX&&a.maxX>=b.minX&&a.minZ<=b.maxZ&&a.maxZ>=b.minZ;
export function footprint(box:Box3):Footprint{return {minX:box.min.x,maxX:box.max.x,minZ:box.min.z,maxZ:box.max.z};}
/** Use rendered mesh bounds, including rails, ladder, stairs and add-ons, in room inches. */
export function bunkAccessZones(bunks:readonly Object3D[],roomToWorld:Matrix4):Footprint[]{
 const inverse=roomToWorld.clone().invert();
 return bunks.map(bunk=>{
  bunk.updateWorldMatrix(true,true);const bounds=new Box3();
  bunk.traverse(object=>{
   const mesh=object as Object3D&{geometry?:{boundingBox:Box3|null;computeBoundingBox:()=>void}};
   if(!mesh.geometry)return;mesh.geometry.computeBoundingBox();
   if(mesh.geometry.boundingBox)bounds.union(mesh.geometry.boundingBox.clone().applyMatrix4(new Matrix4().multiplyMatrices(inverse,object.matrixWorld)));
  });
  const p=footprint(bounds);return {minX:p.minX/.0254-BUNK_ACCESS_BUFFER_INCHES,maxX:p.maxX/.0254+BUNK_ACCESS_BUFFER_INCHES,minZ:p.minZ/.0254-BUNK_ACCESS_BUFFER_INCHES,maxZ:p.maxZ/.0254+BUNK_ACCESS_BUFFER_INCHES};
 });
}
/** Prefer the requested location, then the closest visible free location. Never shrink props.
 * The 240-inch limit is the existing preview workspace, NOT an inferred room dimension.
 * A caller-supplied observed-floor check prevents relocating beyond the original photo. */
export function placeClearFurnishings(items:readonly Furnishing[],zones:readonly Footprint[],boundsFor:(item:Furnishing)=>Footprint,observedFloor:(x:number,z:number)=>boolean,ceilingInches=108):Furnishing[]{
 const accepted:Furnishing[]=[],occupied:{item:Furnishing;bounds:Footprint}[]=[];
 const expand=(b:Footprint,n:number)=>({minX:b.minX-n,maxX:b.maxX+n,minZ:b.minZ-n,maxZ:b.maxZ+n});
 const bunkFootprints=zones.map(zone=>expand(zone,-BUNK_ACCESS_BUFFER_INCHES));
 const drawerAccess=(item:Furnishing,b:Footprint)=>{
  const access={...b},angle=item.turn*Math.PI/180;
  // Reserve 30 inches in front of the actual drawer face.
  access.minX+=Math.min(0,Math.sin(angle)*30);access.maxX+=Math.max(0,Math.sin(angle)*30);
  access.minZ+=Math.min(0,Math.cos(angle)*30);access.maxZ+=Math.max(0,Math.cos(angle)*30);
  return access;
 };
 for(const input of items){
  let item=normalizeFurnishing(input,ceilingInches);
  // Dressers back onto a wall with a one-inch gap, never float in a walkway.
  if(item.kind==='dresser'){
   const side=Math.abs(Math.sin(item.turn*Math.PI/180))>.5;
   const rear=furnishingSize(item).depth/2+1;
   item=normalizeFurnishing({...item,turn:side?90:0,...(side?{x:rear}:{z:rear})},ceilingInches);
  }
  const original=boundsFor(item);
  if(!Object.values(original).every(Number.isFinite))continue;
  const candidates:Furnishing[]=[item];
  for(let x=0;x<=240;x+=12)for(let z=0;z<=240;z+=12){
   if(isWallFurnishing(item)&&(item.wall==='left'?x!==0:z!==0))continue;
   if(item.kind==='dresser'&&(item.turn===90?x!==0:z!==0))continue;
   candidates.push({...item,x:(isWallFurnishing(item)&&item.wall==='left')||(item.kind==='dresser'&&item.turn===90)?item.x:x,z:(isWallFurnishing(item)&&item.wall==='right')||(item.kind==='dresser'&&item.turn===0)?item.z:z});
  }
  candidates.sort((a,b)=>(a.x-item.x)**2+(a.z-item.z)**2-((b.x-item.x)**2+(b.z-item.z)**2));
  for(const proposed of candidates){
   const candidate=normalizeFurnishing(proposed,ceilingInches);
   const dx=isWallFurnishing(item)&&item.wall==='left'?0:candidate.x-item.x,dz=isWallFurnishing(item)&&item.wall==='right'?0:candidate.z-item.z;
   const b={minX:original.minX+dx,maxX:original.maxX+dx,minZ:original.minZ+dz,maxZ:original.maxZ+dz};
   if(b.minX<0||b.minZ<0||b.maxX>240||b.maxZ>240)continue;
   // Initial styling, Retry and click-to-cycle all use this same proximity gate.
   if(bunkFootprints.length&&Math.min(...bunkFootprints.map(bunk=>footprintDistance(b,bunk)))>DECOR_MAX_BUNK_DISTANCE_INCHES[item.kind])continue;
   if(![b.minX,b.maxX].every(x=>[b.minZ,b.maxZ].every(z=>observedFloor(x,z))))continue;
   // Rugs may occupy walking space; solid furniture must preserve bunk access.
   const bunkZones=item.kind==='rug'?zones.map(zone=>expand(zone,-BUNK_ACCESS_BUFFER_INCHES+6)):zones;
   if(bunkZones.some(zone=>overlaps(b,zone)))continue;
   if(item.kind==='dresser'){
    const access=drawerAccess(candidate,b);
    if(access.minX<0||access.minZ<0||access.maxX>240||access.maxZ>240||bunkZones.some(zone=>overlaps(access,zone)))continue;
   }
   if(occupied.some(other=>{
    // A rug is a floor layer, so seating may sit on it.
    if(item.kind==='rug'||other.item.kind==='rug')return false;
    if(item.kind==='ceilingFan'||other.item.kind==='ceilingFan'){const fixture=item.kind==='ceilingFan'?candidate:other.item;const below=item.kind==='ceilingFan'?other.item:candidate;if(fixture.bottom>below.bottom+furnishingSize(below).height+12)return false;}
    // A wall TV can sit above a dresser, with eight inches of vertical separation.
    if(isWallFurnishing(item)||isWallFurnishing(other.item)){
     const tv=isWallFurnishing(item)?candidate:other.item,solid=isWallFurnishing(item)?other.item:candidate;
     if(!isWallFurnishing(solid)&&tv.bottom>=furnishingSize(solid).height+8)return false;
     if(isWallFurnishing(solid)&&(tv.bottom>=solid.bottom+furnishingSize(solid).height+4||solid.bottom>=tv.bottom+furnishingSize(tv).height+4))return false;
    }
    if(overlaps(expand(b,18),other.bounds))return true;
    if(item.kind==='dresser'&&overlaps(drawerAccess(candidate,b),other.bounds))return true;
    return other.item.kind==='dresser'&&overlaps(b,drawerAccess(other.item,other.bounds));
   }))continue;
   accepted.push(candidate);occupied.push({item:candidate,bounds:b});break;
  }
 }
 return accepted;
}

